import { Editor } from '@tiptap/core';
import { Button, Icon } from 'copilot-design-system';
import { Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { TextField } from 'src/components/TextField';
import { URLValidationRegex } from 'src/constants';
import { UrlUtils } from 'src/utils';

const linkSchema = z.object({
  displayText: z.string().optional(),
  linkUrl: z
    .string()
    .regex(URLValidationRegex, 'Please enter a valid URL')
    .or(z.literal('')),
});

type LinkFormValues = z.infer<typeof linkSchema>;

interface Position {
  top: number;
  left: number;
}

interface LinkSelectionPopoverProps {
  editor: Editor;
  isOpen: boolean;
  onClose: () => void;
}

export const LinkSelectionPopover = ({
  editor,
  isOpen,
  onClose,
}: LinkSelectionPopoverProps) => {
  const [position, setPosition] = useState<Position | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [existingUrl, setExistingUrl] = useState('');
  const [existingText, setExistingText] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate position and get existing URL/text when popover opens
  useEffect(() => {
    if (isOpen && editor) {
      const { from, to } = editor.state.selection;
      const start = editor.view.coordsAtPos(from);
      const end = editor.view.coordsAtPos(to);
      const selectionHasText = from !== to;

      setHasSelection(selectionHasText);
      setExistingUrl(editor.getAttributes('link').href ?? '');

      // Get the link text if cursor is on a link
      if (editor.isActive('link')) {
        // Get the text content of the link mark range
        const { from: linkFrom, to: linkTo } = editor.state.selection;
        const $from = editor.state.doc.resolve(linkFrom);
        const linkMark = $from
          .marks()
          .find((mark) => mark.type.name === 'link');
        if (linkMark) {
          // Find the extent of the link mark
          let markFrom = linkFrom;
          let markTo = linkTo;
          editor.state.doc.nodesBetween(
            Math.max(0, linkFrom - 100),
            Math.min(editor.state.doc.content.size, linkTo + 100),
            (node, pos) => {
              if (node.isText && node.marks.some((m) => m.eq(linkMark))) {
                markFrom = Math.min(markFrom, pos);
                markTo = Math.max(markTo, pos + node.nodeSize);
              }
            },
          );
          const linkText = editor.state.doc.textBetween(markFrom, markTo);
          setExistingText(linkText);
        }
      } else {
        setExistingText('');
      }

      // Position above the selection/cursor
      setPosition({
        top: start.top - 8,
        left: selectionHasText ? (start.left + end.right) / 2 : start.left,
      });
    } else {
      setPosition(null);
      setHasSelection(false);
      setExistingUrl('');
      setExistingText('');
    }
  }, [isOpen, editor]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Delay to avoid immediate close from the button click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSubmit = ({ displayText, linkUrl }: LinkFormValues) => {
    if (linkUrl === '') {
      // Remove link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (existingUrl && displayText) {
      // Editing existing link: replace text and update URL
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .deleteSelection()
        .insertContent(
          `<a href="${UrlUtils.GetFullUrl(linkUrl)}">${displayText}</a>`,
        )
        .unsetMark('link')
        .run();
    } else if (!hasSelection && displayText) {
      // No selection, no existing link: insert new link with display text
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${UrlUtils.GetFullUrl(linkUrl)}">${displayText}</a>`,
        )
        .unsetMark('link')
        .run();
    } else {
      // Has selection: set link on selected text
      const { to } = editor.state.selection;
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: UrlUtils.GetFullUrl(linkUrl) })
        .setTextSelection(to)
        .unsetMark('link')
        .run();
    }
    onClose();
  };

  if (!isOpen || !position) return null;

  const initialValues: LinkFormValues = {
    displayText: existingText,
    linkUrl: existingUrl,
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-50 border border-solid border-secondary-hover shadow-dropdown-menu p-3 w-64 bg-white rounded-md"
      style={{
        top: position.top,
        left: position.left,
        transform: hasSelection
          ? 'translate(-50%, -100%)'
          : 'translateY(-100%)',
      }}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={toFormikValidationSchema(linkSchema)}
        validateOnBlur
        validateOnChange
        enableReinitialize
      >
        {({
          errors,
          handleBlur,
          handleChange,
          submitForm,
          touched,
          values,
        }) => (
          <div className="flex flex-row gap-3">
            <div className="flex flex-col gap-2">
              {!hasSelection && (
                <TextField
                  autoFocus={!hasSelection}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <Icon icon="Text" className="h-4 w-4" />,
                  }}
                  className="h-8 text-lg"
                  placeholder="Text to display"
                  name="displayText"
                  value={values.displayText ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={
                    touched.displayText && errors.displayText
                      ? errors.displayText
                      : undefined
                  }
                />
              )}
              <TextField
                variant="outlined"
                InputProps={{
                  startAdornment: <Icon icon="Link" className="h-4 w-4" />,
                }}
                placeholder="Type or paste a link"
                name="linkUrl"
                value={values.linkUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={
                  touched.linkUrl && errors.linkUrl ? errors.linkUrl : undefined
                }
              />
            </div>

            <Button
              className="h-8 self-end"
              disabled={
                !values.linkUrl.trim() ||
                (!hasSelection && !values.displayText?.trim())
              }
              variant="text"
              label={existingUrl ? 'Save' : 'Add'}
              onClick={() => void submitForm()}
            />
          </div>
        )}
      </Formik>
    </div>,
    document.body,
  );
};
